import api from './api';

export interface TipoMoneda {
    codigoMoneda: number;
    descripcion: string;
    simbolo?: string | null;
    cbufce?: string | null;
    aliasfce?: string | null;
    idfce?: string | null;
}

export interface TipoMonedaSearchParams {
    searchTerm?: string;
    pageNumber?: number;
    pageSize?: number;
}

export interface TipoMonedaPagedResult {
    items: TipoMoneda[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface TipoMonedaUpdateDto {
    descripcion: string;
    simbolo?: string | null;
    cbufce?: string | null;
    aliasfce?: string | null;
    idfce?: string | null;
}

const mapItem = (item: Record<string, unknown>): TipoMoneda => ({
    codigoMoneda: Number(item.codigoMoneda ?? item.CodigoMoneda ?? 0),
    descripcion: String(item.descripcion ?? item.Descripcion ?? '').trim(),
    simbolo: (item.simbolo ?? item.Simbolo) != null ? String(item.simbolo ?? item.Simbolo).trim() : null,
    cbufce: (item.cbufce ?? item.Cbufce) != null ? String(item.cbufce ?? item.Cbufce).trim() : null,
    aliasfce: (item.aliasfce ?? item.Aliasfce) != null ? String(item.aliasfce ?? item.Aliasfce).trim() : null,
    idfce: (item.idfce ?? item.Idfce) != null ? String(item.idfce ?? item.Idfce).trim() : null,
});

const tipoMonedaService = {
    getTiposMonedaPaged: async (params?: TipoMonedaSearchParams): Promise<TipoMonedaPagedResult> => {
        const response = await api.get('/api/TipoMoneda', {
            params: {
                SearchTerm: params?.searchTerm || undefined,
                PageNumber: params?.pageNumber ?? 1,
                PageSize: params?.pageSize ?? 10,
            },
        });
        const data = response.data;
        if (data && typeof data === 'object' && 'items' in data) {
            const raw = data as { items: unknown[]; totalCount: number; pageNumber: number; pageSize: number; totalPages: number };
            return {
                items: (raw.items || []).map((i) => mapItem(i as Record<string, unknown>)),
                totalCount: raw.totalCount ?? 0,
                pageNumber: raw.pageNumber ?? 1,
                pageSize: raw.pageSize ?? 10,
                totalPages: raw.totalPages ?? 0,
            };
        }
        const arr = Array.isArray(data) ? data : [];
        return {
            items: arr.map((i) => mapItem(i as Record<string, unknown>)),
            totalCount: arr.length,
            pageNumber: 1,
            pageSize: arr.length || 10,
            totalPages: 1,
        };
    },

    getTiposMoneda: async (): Promise<TipoMoneda[]> => {
        try {
            const all: TipoMoneda[] = [];
            let page = 1;
            let totalPages = 1;
            do {
                const result = await tipoMonedaService.getTiposMonedaPaged({ pageNumber: page, pageSize: 20 });
                all.push(...result.items);
                totalPages = result.totalPages || 1;
                page++;
            } while (page <= totalPages);
            return all;
        } catch (error) {
            console.error('Error al obtener tipos de moneda:', error);
            return [];
        }
    },

    getTipoMoneda: async (codigoMoneda: number): Promise<TipoMoneda> => {
        const response = await api.get(`/api/TipoMoneda/${codigoMoneda}`);
        return mapItem(response.data as Record<string, unknown>);
    },

    getTipoMonedaPorCodigo: async (codigoMoneda: number): Promise<TipoMoneda | null> => {
        try {
            return await tipoMonedaService.getTipoMoneda(codigoMoneda);
        } catch {
            return null;
        }
    },

    createTipoMoneda: async (tipoMoneda: TipoMoneda): Promise<TipoMoneda> => {
        const response = await api.post('/api/TipoMoneda', {
            codigoMoneda: tipoMoneda.codigoMoneda,
            descripcion: tipoMoneda.descripcion,
            simbolo: tipoMoneda.simbolo || null,
            cbufce: tipoMoneda.cbufce || null,
            aliasfce: tipoMoneda.aliasfce || null,
            idfce: tipoMoneda.idfce || null,
        });
        return mapItem(response.data as Record<string, unknown>);
    },

    updateTipoMoneda: async (codigoMoneda: number, dto: TipoMonedaUpdateDto): Promise<void> => {
        await api.put(`/api/TipoMoneda/${codigoMoneda}`, dto);
    },

    deleteTipoMoneda: async (codigoMoneda: number): Promise<void> => {
        await api.delete(`/api/TipoMoneda/${codigoMoneda}`);
    },
};

export default tipoMonedaService;
